import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Bell, BellOff, Clock, Gift, Megaphone, Ticket } from 'lucide-react';
import { getBackendUrl } from '../../../services/origin';
import { isPushSupported } from '../../../services/push';
import { DAY_NAMES } from '../constants';
import { VoucherCard } from '../VoucherCard';
import { formatRedemptionConditionsShort, isRedeemableNow } from '../../../utils/redemptionConditions';

const API = getBackendUrl();

/* ── PREMI TAB ─────────────────────────────────────────────── */
// Unifica ex "Premi" + ex "Coupon": vantaggi tier, premi disponibili,
// coupon attivi, premi campagna vinti, gettoni gioco, riscatto codice
// coupon. Tutto in una unica tab.
export function PremiTab({ data, brand, tc, ts, pushEnabled, onPushToggle, token, pin }) {
  const today = new Date();
  const todayWeekday = today.getDay() === 0 ? 6 : today.getDay() - 1;
  const pc = brand.primary_color;
  const gameTokens = data.game_tokens || 0;
  const couponCreditedRewards = (data.credited_rewards || []).filter(r => r.source === 'coupon');
  const campaignPrizes = (data.campaign_prizes || []).filter(p => p.status === 'available');
  const [couponCode, setCouponCode] = useState('');
  const [redeeming, setRedeeming] = useState(false);
  const [redeemResult, setRedeemResult] = useState(null);
  const [recurringCoupons, setRecurringCoupons] = useState([]);
  const [prefs, setPrefs] = useState(data.push_prefs || { order_status: true, promotions: true });

  const savePref = async (key, value) => {
    const next = { ...prefs, [key]: value };
    setPrefs(next);
    try {
      await axios.post(`${API}/api/my/${token}/push-prefs`, { pin, order_status: next.order_status, promotions: next.promotions });
      toast.success('Preferenze aggiornate');
    } catch {
      toast.error('Impossibile salvare le preferenze');
      setPrefs(prefs); // revert
    }
  };

  const reloadRecurring = async () => {
    try {
      const res = await axios.get(`${API}/api/my/${token}/coupons`, { params: { pin } });
      setRecurringCoupons(res.data.recurring_coupons || []);
    } catch { /* feature optional */ }
  };

  useEffect(() => {
    if (token && pin) reloadRecurring();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, pin]);

  const handleRedeemCoupon = async () => {
    if (!couponCode.trim()) return toast.error('Inserisci un codice coupon');
    setRedeeming(true);
    setRedeemResult(null);
    try {
      const res = await axios.post(`${API}/api/my/${token}/redeem-coupon`, {
        pin: pin, coupon_code: couponCode.trim(),
      });
      setRedeemResult({ success: true, message: res.data.message });
      setCouponCode('');
      toast.success(res.data.message);
      reloadRecurring();
    } catch (err) {
      const msg = err.response?.data?.detail || 'Errore nel riscatto del coupon';
      setRedeemResult({ success: false, message: msg });
      toast.error(msg);
    } finally { setRedeeming(false); }
  };

  const typeLabels = { coupon_bonus: 'Punti Bonus', coupon_discount: 'Sconto', coupon_prize: 'Premio', coupon_voucher: 'Buono', bonus_points: 'Punti', discount: 'Sconto', prize: 'Premio', game_token: 'Gettone' };
  const typeColors = { coupon_bonus: '#3ECF8E', coupon_discount: '#2196F3', coupon_prize: '#9C27B0', coupon_voucher: '#FFAA00', bonus_points: '#3ECF8E', discount: '#2196F3', prize: '#9C27B0', game_token: '#FF9800' };

  return (
    <div className="animate-fade-in-up space-y-6">
      {/* Campaign prizes won (in-venue only — show ribbon "Mostra in cassa") */}
      {campaignPrizes.length > 0 && (
        <section className="" data-testid="campaign-prizes-section">
          <h2 className="text-lg font-bold mb-4 flex items-center gap-2" style={{ color: tc }}>
            <Megaphone size={16} style={{ color: pc }} />
            I tuoi premi vinti dalle campagne
          </h2>
          <div className="space-y-3">
            {campaignPrizes.map(p => (
              <div key={p.id} data-testid={`campaign-prize-${p.id}`}
                className="rounded-3xl p-5 border" style={{ backgroundColor: `${pc}10`, borderColor: `${pc}30` }}>
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${pc}25` }}>
                    <Gift size={22} style={{ color: pc }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm" style={{ color: tc }}>{p.prize_name}</p>
                    <p className="text-xs mt-0.5" style={{ color: ts }}>
                      {p.campaign_name && <span>Campagna: {p.campaign_name}</span>}
                    </p>
                    {p.expires_at && (
                      <p className="text-xs mt-0.5" style={{ color: `${ts}aa` }}>
                        Valido fino al {new Date(p.expires_at).toLocaleDateString('it-IT')}
                      </p>
                    )}
                  </div>
                  <span className="text-xs font-bold px-2.5 py-1 rounded-full flex-shrink-0" style={{ backgroundColor: `${pc}25`, color: pc }}>
                    Mostra in cassa
                  </span>
                </div>
                {p.conditions_summary && (
                  <div className="mt-3 px-3 py-2.5 rounded-xl" data-testid={`prize-conditions-${p.id}`}
                    style={{ backgroundColor: `${pc}15`, border: `1px solid ${pc}30` }}>
                    <p className="text-[10px] font-bold uppercase tracking-wider mb-0.5" style={{ color: pc }}>
                      Quando puoi riscattarlo
                    </p>
                    <p className="text-xs leading-snug" style={{ color: tc }}>{p.conditions_summary}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Tier Benefits */}
      {data.tier_benefits.length > 0 && (
        <section className="" data-testid="tier-benefits-section">
          <h2 className="text-lg font-bold mb-4" style={{ color: tc }}>I tuoi vantaggi {data.tier_names?.[data.tier] || data.tier}</h2>
          <div className="space-y-3">
            {data.tier_benefits.map((b, i) => (
              <div key={i} data-testid={`dashboard-benefit-${i}`} className={`rounded-3xl p-5 ${b.active_today ? '' : 'opacity-60'}`}
                style={{ backgroundColor: `${tc}06`, borderColor: b.active_today ? `${brand.secondary_color}30` : `${tc}08` }}>
                <span className="text-2xl">{b.benefit_type === 'discount' ? '🏷️' : (b.item_emoji || '🌮')}</span>
                <div className="flex-1">
                  <p className="font-semibold text-sm" style={{ color: tc }}>{b.label}</p>
                  {b.description && <p className="text-xs" style={{ color: ts }}>{b.description}</p>}
                  {b.benefit_type === 'free_item' && (
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs" style={{ color: brand.primary_color }}>{b.frequency}</span>
                      <span className="text-xs" style={{ color: ts }}>({b.claimed}/{b.max} ritirato)</span>
                    </div>
                  )}
                  {b.allowed_days && b.allowed_days.length > 0 && b.allowed_days.length < 7 && (
                    <div className="flex gap-1 mt-1.5">
                      {DAY_NAMES.map((d, di) => (
                        <span key={di} className={`text-[9px] px-1.5 py-0.5 rounded ${
                          b.allowed_days.includes(di) ? (di === todayWeekday ? 'font-bold' : '') : ''
                        }`} style={{
                          backgroundColor: b.allowed_days.includes(di) ? (di === todayWeekday ? brand.secondary_color : `${tc}10`) : 'transparent',
                          color: b.allowed_days.includes(di) ? (di === todayWeekday ? brand.background_color : tc) : `${ts}44`,
                        }}>{d}</span>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex-shrink-0">
                  {b.benefit_type === 'discount' && b.active_today && <span className="text-xs font-bold" style={{ color: brand.secondary_color }}>Attivo oggi</span>}
                  {b.benefit_type === 'discount' && !b.active_today && <span className="text-xs" style={{ color: `${ts}66` }}>Non oggi</span>}
                  {b.benefit_type === 'free_item' && b.claimed < b.max && b.active_today && <span className="text-[#FFAA00] text-xs font-bold">Da ritirare</span>}
                  {b.benefit_type === 'free_item' && b.claimed >= b.max && <span className="text-xs" style={{ color: `${ts}66` }}>Ritirato</span>}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Coupon credited rewards (gifts, vouchers, coupons assigned by staff) */}
      {couponCreditedRewards.length > 0 && (
        <section className="" data-testid="credited-coupon-rewards-section">
          <h2 className="text-lg font-bold mb-4" style={{ color: tc }}>I tuoi coupon</h2>
          <div className="space-y-3">
            {couponCreditedRewards.map(r => (
              r.type === 'coupon_voucher' ? (
                <VoucherCard key={r.id} reward={r} token={token} pin={pin} brand={brand} tc={tc} ts={ts} pc={pc} typeColors={typeColors} />
              ) : (() => {
                const conditionsLabel = formatRedemptionConditionsShort(r.redemption_conditions);
                const redeemableNow = isRedeemableNow(r.redemption_conditions, data);
                return (
                  <div key={r.id} data-testid={`credited-coupon-${r.id}`} className="rounded-3xl p-5 border" style={{ backgroundColor: `${tc}06`, borderColor: `${tc}10` }}>
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${typeColors[r.type] || pc}20` }}>
                        <Gift size={22} style={{ color: typeColors[r.type] || pc }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-base font-bold" style={{ color: tc }}>{r.description}</p>
                        <span className="text-xs font-bold px-2.5 py-1 rounded-lg mt-1 inline-block" style={{ backgroundColor: `${typeColors[r.type] || pc}20`, color: typeColors[r.type] || pc }}>
                          {typeLabels[r.type] || r.type}
                        </span>
                      </div>
                    </div>
                    {conditionsLabel && (
                      <div
                        className="mt-3 text-xs font-medium px-3 py-2 rounded-xl flex items-center gap-2"
                        style={{
                          backgroundColor: redeemableNow ? `${brand.secondary_color}12` : '#FCA5A520',
                          color: redeemableNow ? brand.secondary_color : '#DC2626',
                        }}
                        data-testid={`credited-coupon-conditions-${r.id}`}
                      >
                        <Clock size={12} className="flex-shrink-0" />
                        <span className="truncate">{conditionsLabel}</span>
                        {!redeemableNow && <span className="ml-auto whitespace-nowrap font-bold">Non oggi</span>}
                      </div>
                    )}
                  </div>
                );
              })()
            ))}
          </div>
        </section>
      )}

      {/* Recurring coupons */}
      {recurringCoupons.length > 0 && (
        <section className="" data-testid="recurring-coupons-section">
          <h2 className="text-lg font-bold mb-4" style={{ color: tc }}>Coupon ricorrenti</h2>
          <div className="space-y-3">
            {recurringCoupons.map(rc => {
              const periodLabel = rc.period === 'day' ? 'al giorno' : rc.period === 'week' ? 'a settimana' : 'al mese';
              const totalPct = rc.max_total ? Math.min(100, Math.round((rc.total_used / rc.max_total) * 100)) : 0;
              const periodPct = rc.period_max ? Math.min(100, Math.round(((rc.period_used || 0) / rc.period_max) * 100)) : 0;
              const reachedLimit = rc.next_available_label != null;
              return (
                <div key={rc.id} data-testid={`recurring-coupon-${rc.id}`} className="rounded-3xl p-5 border" style={{ backgroundColor: `${tc}06`, borderColor: `${tc}0a` }}>
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold truncate" style={{ color: tc }}>{rc.description || rc.code}</p>
                      <p className="text-xs font-mono mt-0.5" style={{ color: `${ts}88` }}>{rc.code}</p>
                    </div>
                    <span className="text-xs font-semibold px-2 py-0.5 rounded-lg flex-shrink-0"
                      style={{ backgroundColor: reachedLimit ? '#FF525220' : '#3ECF8E20', color: reachedLimit ? '#FF5252' : '#3ECF8E' }}>
                      {reachedLimit ? `Riprova ${rc.next_available_label}` : 'Disponibile ora'}
                    </span>
                  </div>
                  {rc.period && (
                    <div className="mb-2">
                      <div className="flex items-center justify-between text-xs mb-1" style={{ color: `${ts}aa` }}>
                        <span>{rc.period_label && rc.period_label.charAt(0).toUpperCase() + rc.period_label.slice(1)}</span>
                        <span>{rc.period_used || 0} / {rc.period_max} {periodLabel}</span>
                      </div>
                      <div className="h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: `${tc}10` }}>
                        <div className="h-full rounded-full transition-all" style={{ width: `${periodPct}%`, backgroundColor: reachedLimit ? '#FF5252' : pc }} />
                      </div>
                    </div>
                  )}
                  {rc.max_total && (
                    <div>
                      <div className="flex items-center justify-between text-xs mb-1" style={{ color: `${ts}aa` }}>
                        <span>Totale riscatti</span>
                        <span>{rc.total_used} / {rc.max_total}</span>
                      </div>
                      <div className="h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: `${tc}10` }}>
                        <div className="h-full rounded-full transition-all" style={{ width: `${totalPct}%`, backgroundColor: pc }} />
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Self-service coupon redemption */}
      <section className="rounded-3xl p-5 border" style={{ backgroundColor: `${tc}06`, borderColor: `${tc}0a` }} data-testid="self-redeem-coupon-section">
        <p className="text-sm font-semibold mb-1" style={{ color: tc }}>Riscatta un coupon</p>
        <p className="text-xs mb-3" style={{ color: `${ts}88` }}>Hai un codice coupon? Inseriscilo qui o scansiona il QR dalla Home.</p>
        <div className="flex gap-2">
          <input type="text" value={couponCode} onChange={e => setCouponCode(e.target.value.toUpperCase())}
            placeholder="Codice coupon" data-testid="coupon-code-input"
            className="flex-1 h-11 rounded-xl px-4 border text-sm font-mono uppercase focus:outline-none"
            style={{ backgroundColor: brand.background_color, borderColor: `${tc}15`, color: tc }}
            onKeyDown={e => e.key === 'Enter' && handleRedeemCoupon()} />
          <button onClick={handleRedeemCoupon} disabled={redeeming || !couponCode.trim()} data-testid="redeem-coupon-btn"
            className="h-11 px-5 rounded-xl text-sm font-semibold text-white transition-opacity disabled:opacity-40"
            style={{ backgroundColor: pc }}>
            {redeeming ? '...' : 'Riscatta'}
          </button>
        </div>
        {redeemResult && (
          <div className="mt-3 rounded-xl p-3 text-sm font-medium" data-testid="redeem-result" style={{
            backgroundColor: redeemResult.success ? '#3ECF8E15' : '#FF525215',
            color: redeemResult.success ? '#3ECF8E' : '#FF5252',
          }}>
            {redeemResult.message}
          </div>
        )}
      </section>

      {/* Game tokens (only shown if there are some) */}
      {gameTokens > 0 && (
        <section className="rounded-3xl p-5 border" style={{ backgroundColor: `${tc}06`, borderColor: `${tc}0a` }}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold uppercase" style={{ color: ts }}>Gettoni Gioco</p>
              <p className="text-3xl font-bold mt-1" style={{ color: tc }} data-testid="customer-token-balance">{gameTokens}</p>
            </div>
            <div className="w-14 h-14 rounded-2xl flex items-center justify-center" style={{ backgroundColor: '#FF980020' }}>
              <Ticket size={28} style={{ color: '#FF9800' }} />
            </div>
          </div>
        </section>
      )}

      {/* Rewards (point-cost catalog) — McDonald's-style grid: square hero
          per card with the cost overlaid in the bottom-right corner. Tap
          opens the dedicated detail page. */}
      <section className="" data-testid="rewards-section">
        <h2 className="text-lg font-bold mb-4" style={{ color: tc }}>Premi a punti</h2>
        {data.rewards.length === 0 ? (
          <div className="text-center py-8 rounded-2xl border" style={{ backgroundColor: `${tc}04`, borderColor: `${tc}08` }}>
            <Gift size={32} style={{ color: `${ts}44` }} className="mx-auto mb-2" />
            <p className="text-sm" style={{ color: `${ts}88` }}>Nessun premio a punti disponibile.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            {data.rewards.map((r, i) => {
              const ready = r.can_redeem && r.available_today;
              const remaining = Math.max(0, r.points_cost - data.points);
              return (
                <a
                  key={r.id || i}
                  href={`/my/${token}/premio/${r.id}`}
                  data-testid={`dashboard-reward-${i}`}
                  className="block rounded-2xl overflow-hidden border transition-all active:scale-[0.98]"
                  style={{
                    backgroundColor: `${tc}06`,
                    borderColor: ready ? `${brand.primary_color}50` : `${tc}10`,
                  }}
                >
                  {/* Hero — image_url if set, otherwise emoji on brand color */}
                  <div className="relative w-full" style={{ aspectRatio: '1 / 1', backgroundColor: brand.primary_color }}>
                    {r.image_url ? (
                      <img src={r.image_url} alt={r.name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="absolute inset-0 flex items-center justify-center text-[64px]">
                        {r.emoji || '🎁'}
                      </div>
                    )}
                    {/* Cost overlay — bottom right corner */}
                    <div className="absolute bottom-2 right-2 text-right pointer-events-none">
                      <p className="text-2xl font-black tracking-tight leading-none" style={{ color: '#FFFFFF', textShadow: '0 1px 4px rgba(0,0,0,0.4)' }}>
                        {r.points_cost}
                      </p>
                      <p className="text-[9px] font-bold uppercase tracking-widest" style={{ color: '#FFFFFFcc', textShadow: '0 1px 2px rgba(0,0,0,0.4)' }}>
                        punti
                      </p>
                    </div>
                    {/* Status pill */}
                    {ready ? (
                      <div className="absolute top-2 left-2 px-2 py-0.5 rounded-full text-[9px] font-bold backdrop-blur-sm" style={{ backgroundColor: `${brand.secondary_color}dd`, color: '#FFFFFF' }}>
                        Riscattabile
                      </div>
                    ) : !r.available_today && r.can_redeem ? (
                      <div className="absolute top-2 left-2 px-2 py-0.5 rounded-full text-[9px] font-bold backdrop-blur-sm" style={{ backgroundColor: '#FFAA00cc', color: '#FFFFFF' }}>
                        Non oggi
                      </div>
                    ) : null}
                  </div>
                  {/* Body */}
                  <div className="p-3">
                    <p className="font-bold text-sm leading-tight line-clamp-2" style={{ color: tc, minHeight: '2.6em' }}>
                      {r.name}
                    </p>
                    {!ready && remaining > 0 && (
                      <div className="mt-2">
                        <div className="h-1 rounded-full overflow-hidden" style={{ backgroundColor: `${tc}10` }}>
                          <div className="h-full rounded-full" style={{ width: `${Math.min(100, Math.round((data.points / r.points_cost) * 100))}%`, backgroundColor: brand.primary_color }} />
                        </div>
                        <p className="text-xs mt-1 tabular-nums" style={{ color: `${ts}aa` }}>
                          -{remaining} pt
                        </p>
                      </div>
                    )}
                  </div>
                </a>
              );
            })}
          </div>
        )}
      </section>

      {/* Redemption history */}
      {data.redemption_history.length > 0 && (
        <section className="" data-testid="history-section">
          <h2 className="text-lg font-bold mb-4" style={{ color: tc }}>Storico premi riscattati</h2>
          <div className="space-y-1.5">
            {data.redemption_history.map((r, i) => (
              <div key={i} data-testid={`history-item-${i}`} className="flex items-center gap-3 rounded-xl p-3 border" style={{ backgroundColor: `${tc}06`, borderColor: `${tc}08` }}>
                <span className="text-lg">{r.reward_emoji}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate" style={{ color: tc }}>{r.reward_name}</p>
                  <p className="text-xs" style={{ color: `${ts}88` }}>
                    {r.created_at ? new Date(r.created_at).toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' }) : ''}
                  </p>
                </div>
                <span className="text-xs font-medium" style={{ color: brand.primary_color }}>-{r.points_cost} pt</span>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Push toggle */}
      {isPushSupported() && (
        <section className="" data-testid="push-settings-section">
          <div className="rounded-3xl p-5 border" style={{ backgroundColor: `${tc}04`, borderColor: `${tc}08` }}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {pushEnabled ? <Bell size={18} style={{ color: brand.primary_color }} /> : <BellOff size={18} style={{ color: `${ts}66` }} />}
                <div>
                  <p className="text-sm font-medium" style={{ color: tc }}>Notifiche push</p>
                  <p className="text-xs" style={{ color: `${ts}88` }}>{pushEnabled ? 'Ricevi aggiornamenti sul dispositivo' : 'Attiva per non perderti nulla'}</p>
                </div>
              </div>
              <button onClick={onPushToggle} data-testid="push-toggle"
                className="w-12 h-7 rounded-full transition-all relative"
                style={{ backgroundColor: pushEnabled ? brand.primary_color : `${tc}15` }}>
                <div className={`w-5 h-5 bg-white rounded-full absolute top-1 transition-all shadow-sm ${pushEnabled ? 'left-6' : 'left-1'}`} />
              </button>
            </div>
            {pushEnabled && (
              <div className="mt-4 pt-4 border-t space-y-3" style={{ borderColor: `${tc}10` }} data-testid="push-prefs">
                <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: `${ts}66` }}>Cosa vuoi ricevere</p>
                {[
                  ['order_status', 'Stato dei tuoi ordini', 'Confermato, in preparazione, pronto, consegnato'],
                  ['promotions', 'Offerte e promozioni', 'Coupon, novità e sconti dedicati'],
                ].map(([key, label, sub]) => (
                  <div key={key} className="flex items-center justify-between">
                    <div className="pr-3">
                      <p className="text-sm font-medium" style={{ color: tc }}>{label}</p>
                      <p className="text-xs" style={{ color: `${ts}88` }}>{sub}</p>
                    </div>
                    <button onClick={() => savePref(key, !prefs[key])} data-testid={`push-pref-${key}`}
                      className="w-11 h-6 rounded-full transition-all relative flex-shrink-0"
                      style={{ backgroundColor: prefs[key] ? brand.primary_color : `${tc}15` }}>
                      <div className={`w-4 h-4 bg-white rounded-full absolute top-1 transition-all shadow-sm ${prefs[key] ? 'left-6' : 'left-1'}`} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      )}
    </div>
  );
}
